import { useEffect, useState, type FormEvent } from 'react';
import { isApiError } from '../../lib/api';
import {
  eventWebhookUrl,
  getDeliverability,
  KIND_LABEL,
  setEventWebhookKey,
  type DeliverabilityReport,
  type DeliverabilitySummary,
  type Health,
} from '../../lib/deliverability-api';
import styles from './admin.module.css';
import own from './EmailDeliverability.module.css';
import { confirmDialog } from '../../stores/confirm';

// What happened to the email Openline sent — support replies, chat
// transcripts, password resets, invites — as reported by SendGrid's signed
// Event Webhook. Rates are judged against the limits Gmail/Yahoo enforce on
// senders (spam complaints) and the usual bounce ceiling.

const HEALTH_LABEL: Record<Health, string> = {
  'no-data': 'No data yet',
  'low-volume': 'Too few emails to judge',
  good: 'Healthy',
  warning: 'Needs attention',
  critical: 'At risk',
};

const EVENT_LABEL: Record<string, string> = {
  bounce: 'Bounced',
  dropped: 'Dropped',
  spamreport: 'Marked as spam',
  deferred: 'Deferred',
};

const fmtPct = (r: number | null) => (r === null ? '—' : `${(r * 100).toFixed(r > 0 && r < 0.01 ? 2 : 1)}%`);
const fmtNum = (n: number) => n.toLocaleString();

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86_400)} days ago`;
}

function flagFor(s: DeliverabilitySummary, metric: 'bounce' | 'spam' | 'dropped') {
  const a = s.alerts.find((x) => x.metric === metric);
  if (!a) return null;
  return (
    <span className={`${own.flag} ${a.level === 'critical' ? own.flagCritical : ''}`}>
      {a.level === 'critical' ? 'over limit' : 'high'}
    </span>
  );
}

export function EmailDeliverabilitySection() {
  const [days, setDays] = useState(7);
  const [report, setReport] = useState<DeliverabilityReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [keyDraft, setKeyDraft] = useState('');
  const [busy, setBusy] = useState(false);

  async function load(d = days) {
    try {
      setReport(await getDeliverability(d));
      setError(null);
    } catch {
      setError('Could not load deliverability.');
    }
  }
  useEffect(() => {
    void load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  async function saveKey(e: FormEvent, value = keyDraft) {
    e.preventDefault();
    setBusy(true); setError(null); setNotice(null);
    try {
      const r = await setEventWebhookKey(value.trim());
      setNotice(r.keyConfigured ? 'Verification key saved. New SendGrid events will be accepted.' : 'Verification key removed. The event webhook now refuses all posts.');
      setKeyDraft('');
      await load();
    } catch (err) {
      const detail = isApiError(err) && err.body && typeof err.body === 'object' ? (err.body as { detail?: string }).detail : null;
      setError(detail ?? 'Could not save the key.');
    } finally {
      setBusy(false);
    }
  }

  const s = report?.summary;
  const healthClass = s && (s.health === 'good' || s.health === 'warning' || s.health === 'critical') ? own[s.health] : '';

  return (
    <section className={styles.section}>
      <p className={styles.kicker}>Email deliverability</p>
      <p className={own.muted} style={{ margin: '0 0 var(--inic-space-4)' }}>
        What happened to the email Openline sent, as reported by SendGrid: support replies, chat transcripts, password
        resets and invites. Each support reply also shows its own delivered / failed status in the conversation.
      </p>
      {error && <p className={styles.error} role="alert">{error}</p>}
      {notice && <p className={styles.notice} role="status">{notice}</p>}

      <div className={own.toolbar}>
        <select className={styles.select} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Time window">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <button type="button" className={styles.buttonGhost} onClick={() => void load()}>Refresh</button>
        {s && (
          <span className={`${own.health} ${healthClass}`} role="status">
            <span className={own.dot} aria-hidden="true" />
            {HEALTH_LABEL[s.health]}
          </span>
        )}
      </div>

      {s && s.alerts.length > 0 && (
        <ul className={own.alerts}>
          {s.alerts.map((a) => (
            <li key={a.metric} className={a.level === 'critical' ? own.alertCritical : ''}>
              <strong>{a.level === 'critical' ? 'Critical: ' : 'Warning: '}</strong>
              {a.message}.{' '}
              {a.metric === 'spam' && 'Mail providers start filtering a sender above 0.3%. Check who is being emailed and why.'}
              {a.metric === 'bounce' && 'Usually mistyped or stale addresses; check the bounced recipients below.'}
              {a.metric === 'dropped' && 'SendGrid is suppressing these recipients after earlier bounces or spam reports.'}
            </li>
          ))}
        </ul>
      )}

      {s && (
        <div className={own.tiles}>
          <div className={own.tile}>
            <div className={own.tileLabel}>Sent</div>
            <div className={own.tileValue}>{fmtNum(s.sent)}</div>
            <div className={own.tileSub}>accepted by SendGrid</div>
          </div>
          <div className={own.tile}>
            <div className={own.tileLabel}>Delivered</div>
            <div className={own.tileValue}>{fmtPct(s.deliveryRate)}</div>
            <div className={own.tileSub}>{fmtNum(s.delivered)} emails</div>
          </div>
          <div className={own.tile}>
            <div className={own.tileLabel}>Bounced</div>
            <div className={own.tileValue}>{fmtPct(s.bounceRate)}{flagFor(s, 'bounce')}</div>
            <div className={own.tileSub}>{fmtNum(s.bounced)} emails · limit 5%</div>
          </div>
          <div className={own.tile}>
            <div className={own.tileLabel}>Spam reports</div>
            <div className={own.tileValue}>{fmtPct(s.spamRate)}{flagFor(s, 'spam')}</div>
            <div className={own.tileSub}>{fmtNum(s.spamReports)} reports · limit 0.3%</div>
          </div>
          <div className={own.tile}>
            <div className={own.tileLabel}>Dropped</div>
            <div className={own.tileValue}>{fmtPct(s.dropRate)}{flagFor(s, 'dropped')}</div>
            <div className={own.tileSub}>{fmtNum(s.dropped)} suppressed</div>
          </div>
          <div className={own.tile}>
            <div className={own.tileLabel}>Deferred</div>
            <div className={own.tileValue}>{fmtNum(s.deferred)}</div>
            <div className={own.tileSub}>delays, retried by SendGrid</div>
          </div>
        </div>
      )}

      {report && report.byKind.length > 0 && (
        <>
          <p className={own.subhead}>By email type</p>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th className={own.num}>Sent</th>
                  <th className={own.num}>Delivered</th>
                  <th className={own.num}>Bounced</th>
                  <th className={own.num}>Dropped</th>
                  <th className={own.num}>Spam reports</th>
                </tr>
              </thead>
              <tbody>
                {report.byKind.map(({ kind, summary: k }) => (
                  <tr key={kind}>
                    <td>{KIND_LABEL[kind] ?? kind}</td>
                    <td className={own.num}>{fmtNum(k.sent)}</td>
                    <td className={own.num}>{fmtNum(k.delivered)} <span className={own.muted}>({fmtPct(k.deliveryRate)})</span></td>
                    <td className={own.num}>{fmtNum(k.bounced)}</td>
                    <td className={own.num}>{fmtNum(k.dropped)}</td>
                    <td className={own.num}>{fmtNum(k.spamReports)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p className={own.subhead}>Recent problems</p>
      {!report ? (
        <p className={own.muted}>Loading…</p>
      ) : report.problems.length === 0 ? (
        <p className={own.muted}>No bounces, drops, spam reports or deferrals in this window.</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr><th>When</th><th>Recipient</th><th>Type</th><th>What happened</th><th>Reason</th></tr>
            </thead>
            <tbody>
              {report.problems.map((p, i) => (
                <tr key={`${p.at}-${p.email}-${i}`}>
                  <td title={new Date(p.at).toLocaleString()}>{ago(p.at)}</td>
                  <td className={styles.mono}>{p.email}</td>
                  <td>
                    {KIND_LABEL[p.kind ?? 'other'] ?? p.kind}
                    {p.siteName && <span className={own.muted}> · {p.siteName}</span>}
                  </td>
                  <td>
                    {EVENT_LABEL[p.event] ?? p.event}
                    {p.bounceType === 'blocked' && <span className={own.muted}> (blocked)</span>}
                  </td>
                  <td className={own.reason} title={p.reason ?? ''}>
                    {p.smtpStatus && <span className={styles.mono}>{p.smtpStatus} </span>}
                    {p.reason ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className={own.subhead}>SendGrid event webhook</p>
      {report && (
        <p className={own.muted} style={{ margin: '0 0 var(--inic-space-3)' }}>
          {report.webhook.keyConfigured ? (
            <>Verification key set · last event received <strong>{ago(report.webhook.lastEventAt)}</strong>.</>
          ) : (
            <>
              <strong>Not set up.</strong> Without it, nothing on this page updates and support replies stay at “sent”.
            </>
          )}
        </p>
      )}
      <ol className={own.muted} style={{ paddingLeft: 'var(--inic-space-4)', margin: '0 0 var(--inic-space-3)' }}>
        <li>
          In SendGrid → Settings → Mail Settings → <strong>Event Webhook</strong>, set the HTTP Post URL to{' '}
          <code className={styles.mono} style={{ userSelect: 'all' }}>{eventWebhookUrl()}</code>
        </li>
        <li>Select at least: Processed, Delivered, Deferred, Bounced, Dropped, Spam Reports. Enable the webhook.</li>
        <li>Turn on <strong>Signed Event Webhook</strong>, copy the verification key it shows, and paste it below.</li>
      </ol>
      <form className={styles.form} onSubmit={(e) => void saveKey(e)}>
        <label className={styles.field} style={{ flex: '1 1 100%' }}>
          <span className={styles.label}>Verification key</span>
          <textarea
            className={styles.input}
            rows={3}
            value={keyDraft}
            onChange={(e) => setKeyDraft(e.target.value)}
            placeholder={report?.webhook.keyConfigured ? 'A key is set. Paste a new one to replace it.' : 'MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE…'}
            spellCheck={false}
          />
        </label>
        <div className={styles.formActions}>
          <button type="submit" className={styles.button} disabled={busy || !keyDraft.trim()}>Save key</button>
          {report?.webhook.keyConfigured && (
            <button
              type="button"
              className={styles.buttonGhost}
              disabled={busy}
              onClick={async (e) => {
                if (await confirmDialog({ title: 'Remove the verification key?', message: 'SendGrid events will be refused until a key is set again.', confirmLabel: 'Remove key' })) {
                  void saveKey(e as unknown as FormEvent, '');
                }
              }}
            >
              Remove key
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
